import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganizationId } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';

import type { CopyChannel, RequestCopyDraft } from '../components/RequestCopyEditor';
import { composeDraftPreview } from '../copyPreview';

import {
  useGetRequestCopyPreviewQuery,
  useGetRequestCopyQuery,
  useSaveRequestCopyMutation,
} from './api';
import { createWriteQueue } from './writeQueue';

const EMPTY_DRAFT: RequestCopyDraft = { smsBody: '', emailSubject: '', emailBody: '' };

export interface RequestCopyResult {
  draft: RequestCopyDraft;
  smsIsDefault: boolean;
  emailIsDefault: boolean;
  smsSegmentHint?: { characters: number; segments: number };
  /** The token a save is refused for, straight from the response. */
  linkPlaceholder?: string;
  /** The SMS as the patient receives it, link included. */
  smsPreview?: string;
  /** True when the preview shown is the SAVED copy rather than the draft. */
  previewIsStale: boolean;
  isLoading: boolean;
  isDirty: boolean;
  status: 'idle' | 'saving' | 'saved' | 'error';
  errorMessage?: string;
  onChange: (next: RequestCopyDraft) => void;
  onSave: () => void;
  onResetChannel: (channel: CopyChannel) => Promise<void>;
}

/**
 * The Settings default-copy editor's data (AC-11).
 *
 * Autosave rather than a Save button, matching the sibling catalog row and the
 * repo's form convention: local state first, saved on blur.
 */
export const useRequestCopy = (enabled: boolean = true): RequestCopyResult => {
  const organizationId = useOrganizationId();
  const skip = !enabled || !organizationId;
  const orgArg = { orgId: organizationId ?? '' };

  const { data, isLoading } = useGetRequestCopyQuery(orgArg, { skip });
  const { data: preview } = useGetRequestCopyPreviewQuery(orgArg, { skip });
  const [saveCopy] = useSaveRequestCopyMutation();

  const [draft, setDraft] = useState<RequestCopyDraft>(EMPTY_DRAFT);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  /**
   * The server copy the draft was seeded from.
   *
   * Compared before re-seeding so a refetch — a save invalidating the tag, a
   * background revalidation — cannot overwrite what an admin is typing. The
   * same rule as everywhere else on this surface: a response that arrives
   * while the user has moved on does not get to win.
   */
  const seededFrom = useRef<RequestCopyDraft | null>(null);

  useEffect(() => {
    if (!data) return;
    const incoming: RequestCopyDraft = {
      smsBody: data.sms.body,
      emailSubject: data.email.subject,
      emailBody: data.email.body,
    };
    // Compared field by field rather than joined into one string. A separator
    // is a bug waiting to happen — any character can appear in
    // practice-authored copy, so two different states can join to the same
    // string — and the joined version I wrote first embedded NUL bytes, which
    // made git treat this file as binary and hid all 183 lines from the PR
    // diff. Three comparisons need no separator at all.
    const seeded = seededFrom.current;
    if (
      seeded &&
      seeded.smsBody === incoming.smsBody &&
      seeded.emailSubject === incoming.emailSubject &&
      seeded.emailBody === incoming.emailBody
    ) {
      return;
    }

    /**
     * Per field, not per document (Greptile, #2942).
     *
     * A field the admin has not touched since it was seeded takes the server's
     * value; a field they have edited keeps theirs. Wholesale re-seeding meant
     * the refetch that follows a reset — the one that has to arrive, to learn
     * the default text — discarded whatever they typed in the OTHER channel
     * while the reset was in flight.
     *
     * The rule is not specific to resets: it is the same for a save
     * invalidating the tag or a background revalidation, and it needs no
     * knowledge of which of those happened.
     */
    const keepEdits = (current: string, was: string | undefined, next: string) =>
      was === undefined || current === was ? next : current;

    setDraft(current => ({
      smsBody: keepEdits(current.smsBody, seeded?.smsBody, incoming.smsBody),
      emailSubject: keepEdits(
        current.emailSubject,
        seeded?.emailSubject,
        incoming.emailSubject,
      ),
      emailBody: keepEdits(current.emailBody, seeded?.emailBody, incoming.emailBody),
    }));
    seededFrom.current = incoming;
    // The server has now spoken for these channels, so the reset marks that
    // made earlier payloads omit them have done their job.
    resetPendingRef.current.clear();
  }, [data]);

  /**
   * The latest draft, readable at save time.
   *
   * `useAutoSave` captures its payload by value when the debounce is armed, so
   * a save that fires two seconds later sends the draft as it was two seconds
   * ago. That is the mechanism behind the reset collision: the payload was
   * built before the reset and carries the pre-reset body.
   */
  const draftRef = useRef<RequestCopyDraft>(EMPTY_DRAFT);
  draftRef.current = draft;

  /**
   * Channels whose reset has been asked for but not yet confirmed by a
   * refetch. Every payload built while a channel is in here OMITS it, which is
   * how this endpoint spells "restore the default".
   *
   * Ordering the writes was not enough (jotreviewer, #2942). The queue decides
   * when a write lands, not what it says — and a save queued after the reset
   * still said "the body is <the customized text>", so the reset landed first
   * and was then faithfully undone. Marking the channel changes what every
   * later payload says, which is the only thing that closes this as a class.
   */
  const resetPendingRef = useRef<Set<CopyChannel>>(new Set());

  /** The whole document, minus any channel waiting on its reset. */
  const buildUpdate = useCallback((next: RequestCopyDraft) => {
    const resetting = resetPendingRef.current;
    return {
      ...(resetting.has('sms') ? {} : { sms: { body: next.smsBody } }),
      ...(resetting.has('email')
        ? {}
        : { email: { subject: next.emailSubject, body: next.emailBody } }),
    };
  }, []);

  const pendingRef = useRef<RequestCopyDraft | null>(null);

  /**
   * Every write to this endpoint goes through one queue, so they land in the
   * order they were asked for.
   *
   * Pairwise ordering was not enough (Greptile, #2942): cancelling the queued
   * autosave before a reset stops THAT collision, and an edit made while the
   * reset is still in flight simply starts a new write that races it. The
   * endpoint takes the whole document, so overlapping writes do not merge —
   * the later one wins wholesale, whichever it happens to be.
   */
  const runWriteRef = useRef(createWriteQueue());
  const runWrite = runWriteRef.current;

  /**
   * Registered as `useAutoSave`'s onSave, so it runs on BOTH paths — the 2s
   * debounce calls it directly, and blur calls it through
   * `saveImmediatelyAndWait`.
   *
   * That is why the pending marker is cleared HERE and nowhere else
   * (jotreviewer, #2942). Clearing it only in the blur handler left a
   * debounced save succeeding with the amber "unsaved" indicator still lit,
   * and the next blur then re-sent a payload the server already had.
   */
  const performSave = useCallback(
    async (next: RequestCopyDraft) => {
      if (!organizationId) return;
      setErrorMessage(undefined);
      try {
        // Built inside the queued operation, not before it: the reset marks
        // and the draft can both change between arming a save and running it,
        // and the payload should say what is true when it is sent.
        await runWrite(() => {
          const sending = draftRef.current;
          return saveCopy({
            orgId: organizationId,
            requestCopyUpdate: buildUpdate(sending),
          }).unwrap();
        });
        // Only if nothing newer is waiting: a keystroke during the request
        // leaves a different draft pending, and that one is still unsaved.
        if (pendingRef.current === next) pendingRef.current = null;
      } catch (error) {
        // The save is refused when the copy contains the secure link. The
        // editor warns before this point; this is the backstop, and it keeps
        // the admin's text on screen so the remedy can be applied.
        const detail = (error as { data?: { message?: string } })?.data?.message;
        setErrorMessage(
          detail ?? 'That could not be saved. Check the message and try again.',
        );
        throw error;
      }
    },
    [organizationId, saveCopy, runWrite, buildUpdate],
  );

  const {
    triggerAutoSave,
    saveImmediatelyAndWait,
    cancelPendingSave,
    status,
  } = useAutoSave<RequestCopyDraft>({
    onSave: performSave,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  const onChange = useCallback(
    (next: RequestCopyDraft) => {
      // Typing into a channel takes back its reset: the admin is customizing
      // it again, so later payloads must carry what they wrote rather than
      // continuing to omit the channel.
      const previous = draftRef.current;
      if (next.smsBody !== previous.smsBody) resetPendingRef.current.delete('sms');
      if (
        next.emailSubject !== previous.emailSubject ||
        next.emailBody !== previous.emailBody
      ) {
        resetPendingRef.current.delete('email');
      }

      draftRef.current = next;
      setDraft(next);
      pendingRef.current = next;
      triggerAutoSave(next);
    },
    [triggerAutoSave],
  );

  /**
   * Blur saves what is pending. It does not clear the pending marker —
   * `performSave` does, on both paths — and a rejection deliberately leaves it
   * pending so the draft stays unsaved and retryable rather than looking saved.
   */
  const onSave = useCallback(() => {
    if (!pendingRef.current) return;
    saveImmediatelyAndWait(pendingRef.current).catch(() => {
      // Reported through `errorMessage`; the draft stays pending.
    });
  }, [saveImmediatelyAndWait]);

  /**
   * Reset one channel by OMITTING it: the endpoint restores a channel's seed
   * when the save leaves it out. The other channel is sent as it stands, so
   * resetting the text message cannot discard an email the practice wrote.
   */
  const onResetChannel = useCallback(
    async (channel: CopyChannel) => {
      if (!organizationId) return;
      // Serialize against autosave, or the reset is undone by a write it did
      // not know about: a queued autosave carries the full draft, including
      // the channel just reset, and would put the old text straight back.
      // Drop anything queued, then let anything already in flight land first.
      cancelPendingSave();
      pendingRef.current = null;

      // Marked BEFORE the write, so a save that fires while this is in flight
      // omits the channel too instead of restating the old body.
      resetPendingRef.current.add(channel);

      await runWrite(() =>
        saveCopy({
          orgId: organizationId,
          requestCopyUpdate: buildUpdate(draftRef.current),
        }).unwrap(),
      ).catch(() => {
        // Un-marked, or the channel would be omitted from every later save and
        // stay stuck at its default however many times it is re-edited.
        resetPendingRef.current.delete(channel);
        setErrorMessage('That could not be reset. Please try again.');
      });

      // The re-seed is per field now, so the refetch that follows brings the
      // default text for this channel without touching the other one — no need
      // to forget what the draft was seeded from.
    },
    [organizationId, saveCopy, cancelPendingSave, runWrite, buildUpdate],
  );

  const savedSms = data?.sms.body;
  const composedSms = preview?.sms.body;

  const draftPreview =
    savedSms !== undefined && composedSms !== undefined
      ? composeDraftPreview(draft.smsBody, savedSms, composedSms)
      : null;

  return {
    draft,
    // Per channel, so resetting one does not offer to discard the other.
    smsIsDefault: data?.sms.is_default ?? true,
    emailIsDefault: data?.email.is_default ?? true,
    smsSegmentHint: data
      ? { characters: data.sms.characters, segments: data.sms.segments }
      : undefined,
    linkPlaceholder: data?.link_placeholder,
    smsPreview: draftPreview ?? composedSms,
    // True only when the fallback is in play: the derivation failed, so what is
    // on screen is the saved message rather than what is being typed.
    previewIsStale: draftPreview === null && composedSms !== undefined,
    isLoading: !skip && isLoading,
    isDirty: pendingRef.current !== null,
    status,
    errorMessage,
    onChange,
    onSave,
    onResetChannel,
  };
};
